// frontend/src/pages/Tickets/CreateTicketForm.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Form, Input, Select, Button, DatePicker, Typography,
  Row, Col, Card, Checkbox, Tooltip, Upload, message, Spin, Modal,
  Alert, Divider
} from 'antd';
import {
  SendOutlined, UploadOutlined, CloseCircleOutlined, ClearOutlined
} from '@ant-design/icons';
import { Editor } from '@tinymce/tinymce-react';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import api from '../../utils/api';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useNotificationApi } from '../../context/NotificationContext';
import './CreateTicketForm.css';

const { Option } = Select;
const { Title, Text } = Typography;

// Safe data sanitization function to prevent circular references
const safeSanitize = (data) => {
  if (!data) return data;
  
  try {
    // Use JSON methods to break circular references
    return JSON.parse(JSON.stringify(data));
  } catch (error) {
    console.warn('Data sanitization warning:', error);
    // Fallback: manually extract safe properties
    if (Array.isArray(data)) {
      return data.map(item => {
        if (item && typeof item === 'object') {
          const safeItem = {};
          // Only include safe, non-circular properties
          const safeProps = ['id', 'name', 'code', 'contractName', 'contract_name', 
                           'productCategoryId', 'product_category_id', 'sort_order',
                           'default_priority_id', 'default_resolution_time_value'];
          safeProps.forEach(prop => {
            if (item[prop] !== undefined) {
              safeItem[prop] = item[prop];
            }
          });
          return safeItem;
        }
        return item;
      });
    }
    return data;
  }
};

const CreateTicketForm = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user, hasRole, ROLE_DEFINITIONS } = useAuth();
  const { appSettings } = useAppContext();
  const notificationApi = useNotificationApi();

  // State management
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [descriptionHtml, setDescriptionHtml] = useState('<p></p>');
  const editorRef = useRef(null);
  const [initialDataLoading, setInitialDataLoading] = useState(true);

  // Data states - initialized with safe empty arrays
  const [contracts, setContracts] = useState([]);
  const [allTicketCategories, setAllTicketCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [contractProducts, setContractProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [displayedPriorities, setDisplayedPriorities] = useState([]);

  // Dependent dropdown states
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [contractSpecificCategories, setContractSpecificCategories] = useState([]);

  // Date formats
  const dateFormat = appSettings?.dateFormat || 'YYYY-MM-DD';
  const timeFormat = appSettings?.timeFormat || 'HH:mm:ss';
  const displayDateFormat = `${dateFormat} ${timeFormat}`;

  // FIXED: Safe extractArrayData function
  const extractArrayData = useCallback((response, expectedKey = null) => {
    if (!response || !response.data) return [];
    
    let data;
    if (Array.isArray(response.data)) {
      data = response.data;
    } else if (typeof response.data === 'object' && Array.isArray(response.data.data)) {
      data = response.data.data;
    } else if (expectedKey && typeof response.data === 'object' && Array.isArray(response.data[expectedKey])) {
      data = response.data[expectedKey];
    } else {
      return [];
    }

    // Apply safe sanitization to prevent circular references
    return safeSanitize(data);
  }, []);

  // FIXED: Using ref for priorities to avoid state dependency issues
  const prioritiesRef = useRef([]);

  // Notification helpers (unchanged)
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((messageContent, key = 'globalLoading') => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);

  const toSnakeCase = (obj) => {
    const newObj = {};
    Object.keys(obj).forEach(key => {
      const newKey = key
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase();
      newObj[newKey] = obj[key];
    });
    return newObj;
  };

  const handleProductCategoryChange = useCallback((selectedId) => {
    form.setFieldsValue({ productId: undefined });

    const newFilteredProducts = contractProducts.filter(product => {
      return String(product.productCategoryId) === String(selectedId);
    });

    setFilteredProducts(newFilteredProducts);
  }, [form, contractProducts]);

  const fetchContractSla = useCallback(async (contractId, categoryId) => {
    try {
      const response = await api.get(`/api/contracts/${contractId}/categories/${categoryId}/sla`);
      return safeSanitize(response.data.data);
    } catch (error) {
      console.error(`Failed to fetch SLA for contract ${contractId} and category ${categoryId}:`, error);
      return null;
    }
  }, []);

  // Updated function to fetch contract-specific data, including products
  const handleContractChange = useCallback(async (selectedContractId) => {
    setSelectedContractId(selectedContractId);
    form.setFieldsValue({
      ticketCategoryId: undefined,
      productCategoryId: undefined,
      productId: undefined,
      priorityId: undefined,
      agreedResolutionPeriodValue: undefined
    });
    setContractSpecificCategories([]);
    setContractProducts([]);
    setFilteredProducts([]);

    if (selectedContractId) {
      const loadingKey = 'fetchContractData';
      const hideLoading = showLoading(loadingKey, 'Loading contract-specific data...');
      try {
        const contractSlasRes = await api.get(`/api/contracts/${selectedContractId}/sla-definitions`);
        const contractSlas = extractArrayData(contractSlasRes, 'data');

        const categoriesWithSlaIds = new Set(contractSlas.map(sla => String(sla.ticketCategoryId)));
        const categoriesWithSla = allTicketCategories.filter(cat => categoriesWithSlaIds.has(String(cat.id)));
        setContractSpecificCategories(safeSanitize(categoriesWithSla));

        const contractProductsRes = await api.get(`/api/contracts/${selectedContractId}/products`);
        const fetchedContractProducts = extractArrayData(contractProductsRes, 'data').map(product => ({
          ...product,
          productCategoryId: product.product_category_id || product.productCategoryId,
        }));
        setContractProducts(fetchedContractProducts);
        
        const currentProductCategoryId = form.getFieldValue('productCategoryId');
        if (currentProductCategoryId) {
            const newFilteredProducts = fetchedContractProducts.filter(product =>
              String(product.productCategoryId) === String(currentProductCategoryId)
            );
            setFilteredProducts(newFilteredProducts);
        } else {
            setFilteredProducts([]);
        }

      } catch (error) {
        console.error('Error fetching contract-specific data:', error);
        showError('Failed to Load Data', 'Could not load contract-specific details.', 'fetchContractDataError');
      } finally {
        hideLoading();
      }
    }
  }, [form, showError, showLoading, extractArrayData, allTicketCategories]);

  const handleTicketCategoryChange = useCallback(async (selectedId) => {
    form.setFieldsValue({
      priorityId: undefined,
      agreedResolutionPeriodValue: undefined,
    });

    const selectedCategory = allTicketCategories.find(cat => String(cat.id) === String(selectedId));

    if (selectedContractId && selectedCategory) {
      const loadingKey = 'fetchSlaData';
      const hideLoading = showLoading(loadingKey, 'Loading SLA details...');
      try {
        const contractSla = await fetchContractSla(selectedContractId, selectedId);

        let prioritiesToDisplay = prioritiesRef.current;
        if (contractSla) {
          form.setFieldsValue({
            priorityId: contractSla.priorityId,
            agreedResolutionPeriodValue: contractSla.agreedResolutionTimeValue,
          });
          prioritiesToDisplay = prioritiesRef.current.filter(p => p.id === contractSla.priorityId);
        } else {
          form.setFieldsValue({
            priorityId: undefined,
            agreedResolutionPeriodValue: undefined,
          });
        }

        const sortedPriorities = prioritiesToDisplay.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setDisplayedPriorities(sortedPriorities);

      } catch (error) {
        console.error('Error in handleTicketCategoryChange:', error);
        showError('Failed to Load SLA', 'Could not load custom SLA for this category.', 'fetchSlaError');
      } finally {
        hideLoading();
      }
    } else {
      const sortedPriorities = prioritiesRef.current.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setDisplayedPriorities(sortedPriorities);
    }
  }, [form, selectedContractId, allTicketCategories, showLoading, showError, fetchContractSla]);

  const handleClearDescription = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.setContent('<p></p>');
      setDescriptionHtml('<p></p>');
      showSuccess('Description Cleared', 'The description field has been cleared.');
    }
  }, [showSuccess]);

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList.filter(file => !file.status || file.status !== 'error'));
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => onSuccess("ok"), 0);
  };

  const handleSubmit = async (values) => {
    const loadingKey = 'createTicket';
    const hideLoading = showLoading(loadingKey, 'Creating ticket...');
    setLoading(true);

    try {
      const formData = new FormData();

      const fieldsToSubmit = {
        contractId: values.contractId,
        ticketCategoryId: values.ticketCategoryId,
        productCategoryId: values.productCategoryId,
        productId: values.productId,
        title: values.title,
        caseReportedDate: values.caseReportedDate,
        priorityId: values.priorityId,
        ticketTypeId: values.ticketTypeId,
        dueDate: values.dueDate,
        agreedToExtendTime: values.agreedToExtendTime,
      };

      const snakeCaseValues = toSnakeCase(fieldsToSubmit);

      Object.entries(snakeCaseValues).forEach(([key, value]) => {
        if (dayjs.isDayjs(value)) {
          value = moment(value.toDate()).tz('Asia/Kuala_Lumpur').format('YYYY-MM-DD HH:mm:ss');
        }
        if (key === 'agreed_to_extend_time') {
          value = value ? '1' : '0';
        }
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      formData.append('description', descriptionHtml);
      fileList.forEach(file => {
        if (file.originFileObj) {
          formData.append('attachments', file.originFileObj);
        }
      });

      const response = await api.post('/api/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showSuccess('Ticket Created', 'Your ticket has been created successfully!');

      form.resetFields();
      setDescriptionHtml('<p></p>');
      setFileList([]);
      if (editorRef.current) editorRef.current.setContent('<p></p>');

      navigate('/tickets');
    } catch (error) {
      console.error('Ticket creation error:', error);

      let errorMessage = 'Failed to create ticket. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      showError('Creation Failed', errorMessage);

      if (error.response?.data?.errors) {
        const fieldErrors = Object.entries(error.response.data.errors).map(([field, messages]) => ({
          name: field,
          errors: Array.isArray(messages) ? messages : [messages]
        }));
        form.setFields(fieldErrors);
      }
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      const loadingKey = 'fetchTicketData';
      const hideLoading = showLoading(loadingKey, 'Loading form options...');
      setInitialDataLoading(true);

      try {
        let contractsRes;
        if (user?.user_type === 'customer' || user?.user_type === 'partner') {
          contractsRes = await api.get(`/api/contracts/my-contracts/${user.organization_id}`);
        } else {
          contractsRes = await api.get('/api/contracts');
        }

        const [
          allTicketCategoriesRes,
          prioritiesRes,
          ticketTypesRes,
          productCategoriesRes,
        ] = await Promise.all([
          api.get('/api/ticket-categories'),
          api.get('/api/priorities'),
          api.get('/api/ticket-types'),
          api.get('/api/product-categories'),
        ]);

        // Apply safe sanitization to all data
        const fetchedContracts = extractArrayData(contractsRes, 'contracts').map(contract => ({
          id: contract.id,
          contractName: contract.contractName || contract.contract_name
        }));
        setContracts(fetchedContracts);

        const fetchedProductCategories = extractArrayData(productCategoriesRes, 'productCategories');
        setProductCategories(fetchedProductCategories);

        const fetchedPriorities = extractArrayData(prioritiesRes, 'priorities');
        setPriorities(fetchedPriorities);
        // Store in ref to avoid dependency issues
        prioritiesRef.current = fetchedPriorities;
        const sortedPriorities = fetchedPriorities.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        setDisplayedPriorities(sortedPriorities);

        const fetchedTicketTypes = extractArrayData(ticketTypesRes, 'ticketTypes');
        const customTypeOrder = ["Corrective Maintenance", "Preventive Maintenance", "Others"];
        const sortedTicketTypes = fetchedTicketTypes.sort((a, b) => {
          const indexA = customTypeOrder.indexOf(a.name);
          const indexB = customTypeOrder.indexOf(b.name);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.name.localeCompare(b.name);
        });
        setTicketTypes(sortedTicketTypes);

        const categories = extractArrayData(allTicketCategoriesRes, 'ticketCategories').map(cat => ({
          id: cat.id,
          name: cat.name,
          assignedPriorities: cat.assigned_priorities || [],
          defaultPriorityId: cat.default_priority_id,
          defaultResolutionTimeValue: cat.default_resolution_time_value
        }));
        setAllTicketCategories(categories);

      } catch (error) {
        console.error('Error fetching form data:', error);
        showError('Failed to Load Form', 'Could not load required form data. Please try again.');
      } finally {
        hideLoading();
        setInitialDataLoading(false);
      }
    };
    fetchInitialData();
  }, [user, extractArrayData, showError, showLoading]);

  if (initialDataLoading) {
    return (
      <div className="create-ticket-page">
        <Spin size="large" tip="Loading form..." style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }} />
      </div>
    );
  }

  return (
    <div className="create-ticket-page">
      {/* Header Section */}
      <div className="create-ticket-header">
        <Title level={2} className="create-ticket-title">Create New Support Ticket</Title>
        <Text className="create-ticket-description">
          Fill in the details below to create a new helpdesk ticket. All fields marked with * are required.
        </Text>
      </div>

      {/* Main Content */}
      <div className="create-ticket-main">
        <Card className="create-ticket-card">
          <div className="create-ticket-content">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="create-ticket-form"
            >
              {/* Contract & Product Information */}
              <div className="form-section">
                <Title level={4} className="section-title">Contract & Product Information</Title>
                <div className="form-item-container">
                  <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="contractId"
                        label="Contract *"
                        rules={[{ required: true, message: 'Please select a contract!' }]}
                      >
                        <Select
                          placeholder="Select the relevant contract"
                          className="form-control"
                          allowClear={false}
                          showSearch
                          onChange={handleContractChange}
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {contracts.map(contract => (
                            <Option key={contract.id} value={contract.id}>
                              {contract.contractName}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="ticketCategoryId"
                        label="Ticket Category *"
                        rules={[{ required: true, message: 'Please select a ticket category!' }]}
                      >
                        <Select
                          placeholder="Select a category for the ticket"
                          className="form-control"
                          allowClear={false}
                          showSearch
                          onChange={handleTicketCategoryChange}
                          disabled={!form.getFieldValue('contractId')}
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {contractSpecificCategories.map(category => (
                            <Option key={category.id} value={category.id}>
                              {category.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="productCategoryId"
                        label="Product Category *"
                        rules={[{ required: true, message: 'Please select a product category!' }]}
                      >
                        <Select
                          placeholder="Select product category for the issue"
                          className="form-control"
                          onChange={handleProductCategoryChange}
                          allowClear={false}
                          showSearch
                          disabled={!form.getFieldValue('contractId')}
                          filterOption={(input, option) => {
                            const productCategory = productCategories.find(p => String(p.id) === String(option.value));
                            return (
                              option.children.toLowerCase().includes(input.toLowerCase()) ||
                              (productCategory?.code?.toLowerCase().includes(input.toLowerCase()) || false)
                            );
                          }}
                        >
                          {productCategories.map(pCat => (
                            <Option key={pCat.id} value={pCat.id}>
                              {pCat.name} {pCat.code ? `| ${pCat.code}` : ''}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="productId"
                        label="Product *"
                        rules={[{ 
                          required: true, 
                          message: 'Please select a product!',
                          validator: (_, value) => {
                            if (filteredProducts.length > 0 && !value) {
                              return Promise.reject(new Error('Please select a product!'));
                            }
                            return Promise.resolve();
                          }
                        }]}
                      >
                        <Select
                          placeholder={
                            filteredProducts.length === 0 
                              ? "No products available for selected category" 
                              : "Select the specific product"
                          }
                          className="form-control"
                          allowClear={false}
                          showSearch
                          filterOption={(input, option) => {
                            const product = filteredProducts.find(p => String(p.id) === String(option.value));
                            return (
                              option.children.toLowerCase().includes(input.toLowerCase()) ||
                              (product?.code?.toLowerCase().includes(input.toLowerCase()) || false)
                            );
                          }}
                          disabled={!form.getFieldValue('productCategoryId') || filteredProducts.length === 0}
                        >
                          {filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                              <Option key={product.id} value={product.id}>
                                {product.name} {product.code ? `| ${product.code}` : ''}
                              </Option>
                            ))
                          ) : (
                            <Option key="no-products" value="no-products" disabled>
                              No products available for this category
                            </Option>
                          )}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>

              <Divider />

              {/* Ticket Details */}
              <div className="form-section">
                <Title level={4} className="section-title">Ticket Details</Title>
                <div className="form-item-container">
                  <Form.Item
                    name="title"
                    label="Ticket Title *"
                    rules={[{ required: true, message: 'Please enter a ticket title!' }]}
                  >
                    <Input
                      placeholder="Brief summary of the issue"
                      className="form-control"
                    />
                  </Form.Item>
                  
                  <div className="editor-section">
                    <Form.Item
                      label="Description *"
                      rules={[{
                        validator: () => {
                          if (descriptionHtml.trim() === '<p></p>' || descriptionHtml.trim() === '') {
                            return Promise.reject(new Error('Please enter a detailed description!'));
                          }
                          return Promise.resolve();
                        },
                      }]}
                    >
                      <div className="editor-container">
                        <Editor
                          tinymceScriptSrc="/tinymce/tinymce.min.js"
                          onInit={(evt, editor) => editorRef.current = editor}
                          value={descriptionHtml}
                          onEditorChange={setDescriptionHtml}
                          init={{
                            license_key: 'gpl',
                            base_url: '/tinymce',
                            suffix: '.min',
                            height: 300,
                            menubar: false,
                            plugins: [
                              'lists', 'code', 'advlist', 'autolink', 'link', 'image', 'charmap',
                              'wordcount', 'searchreplace', 'visualblocks', 'fullscreen',
                              'insertdatetime', 'table', 'media', 'help'
                            ],
                            toolbar: 'undo redo | formatselect | bold italic backcolor | ' +
                                     'alignleft aligncenter alignright alignjustify | ' +
                                     'bullist numlist outdent indent | link image charmap | ' +
                                     'searchreplace visualblocks fullscreen | insertdatetime media table | code wordcount help | removeformat',
                            content_style: 'body { font-family:Inter,sans-serif; font-size:14px }',
                          }}
                        />
                      </div>
                      <div className="editor-actions">
                        <Button
                          type="default"
                          icon={<ClearOutlined />}
                          onClick={handleClearDescription}
                          className="clear-description-btn"
                        >
                          Clear Description
                        </Button>
                      </div>
                    </Form.Item>
                  </div>
                </div>
              </div>

              <Divider />

              {/* Priority & Scheduling */}
              <div className="form-section">
                <Title level={4} className="section-title">Priority & Scheduling</Title>
                <div className="form-item-container">
                  <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="caseReportedDate"
                        label="Case Reported Date & Time *"
                        rules={[{ required: true, message: 'Please select the reported date and time!' }]}
                      >
                        <DatePicker
                          showTime={{ format: 'hh:mm A', use12Hours: true }}
                          format={displayDateFormat}
                          className="form-control"
                          placeholder="Select the date and time the issue was reported"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="priorityId"
                        label="Priority *"
                        rules={[{ required: true, message: 'Please select a priority!' }]}
                      >
                        <Select
                          placeholder="Select ticket priority"
                          className="form-control"
                          allowClear={false}
                          showSearch
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          disabled={!form.getFieldValue('ticketCategoryId')}
                        >
                          {displayedPriorities.map(priority => (
                            <Option key={priority.id} value={priority.id}>
                              {priority.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="ticketTypeId"
                        label="Ticket Type *"
                        rules={[{ required: true, message: 'Please select a ticket type!' }]}
                      >
                        <Select
                          placeholder="Select the type of ticket"
                          className="form-control"
                          allowClear={false}
                          showSearch
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {ticketTypes.map(type => (
                            <Option key={type.id} value={type.id}>
                              {type.name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="dueDate"
                        label="Due Date & Time"
                      >
                        <DatePicker
                          showTime={{ format: 'hh:mm A', use12Hours: true }}
                          format={displayDateFormat}
                          className="form-control"
                          placeholder="Select the ticket's due date and time"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={[24, 16]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="agreedResolutionPeriodValue"
                        label="Agreed Resolution Period (Hours)"
                      >
                        <Input
                          type="number"
                          min={0}
                          placeholder="e.g., 24"
                          className="form-control"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="agreedToExtendTime"
                        valuePropName="checked"
                        style={{ marginBottom: 0 }}
                      >
                        <Checkbox className="agreement-checkbox">
                          <Text strong>Agreed to Extend Time?</Text>
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              </div>

              <Divider />

              {/* Attachments */}
              <div className="form-section">
                <Title level={4} className="section-title">Attachments</Title>
                <Form.Item className="upload-section">
                  <Upload
                    name="attachments"
                    multiple={true}
                    fileList={fileList}
                    onChange={handleFileChange}
                    beforeUpload={() => false}
                    customRequest={dummyRequest}
                    showUploadList={{
                      showRemoveIcon: true,
                      removeIcon: <CloseCircleOutlined style={{ color: 'red' }} />,
                    }}
                    listType="text"
                  >
                    <Button icon={<UploadOutlined />} className="upload-button">
                      Select File(s)
                    </Button>
                  </Upload>
                  <Text type="secondary" className="upload-hint">
                    You can upload multiple files (max 5MB per file). Supported formats: PDF, DOC, DOCX, JPG, PNG.
                  </Text>
                </Form.Item>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <Button 
                  type="default" 
                  onClick={() => navigate('/tickets')}
                  className="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SendOutlined />}
                  className="submit-button"
                >
                  Create Ticket
                </Button>
              </div>
            </Form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreateTicketForm;